import { Check, Code, Copy, ExternalLink, Send } from 'lucide-react';
import React, { useState } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useMerchantAuth } from '../contexts/MerchantAuthContext';
import { useMerchantPaymentPolling } from '../hooks/useMerchantPaymentPolling';
import { AnalyticsProperties, ANALYTICS_EVENTS, trackAnalyticsEvent } from '../lib/analytics';
import { API_BASE_URL, RequisiteType } from '../lib/api';
import { paymentWebUrl } from '../lib/domains';
import { merchantEvents, MERCHANT_EVENTS } from '../lib/merchantEvents';

const createMerchantId = (prefix: string) =>
    `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const formatApiErrorValue = (value: unknown): string | null => {
    if (typeof value === 'string') {
        return value.trim() || null;
    }
    if (Array.isArray(value)) {
        const messages = value
            .map((item) => {
                if (item && typeof item === 'object') {
                    const record = item as Record<string, unknown>;
                    const message = formatApiErrorValue(record.msg ?? record.message ?? record.detail);
                    const location = Array.isArray(record.loc)
                        ? record.loc.filter((part) => part !== 'body').join('.')
                        : '';
                    if (message) {
                        return location ? `${location}: ${message}` : message;
                    }
                }
                return formatApiErrorValue(item);
            })
            .filter((message): message is string => Boolean(message));
        return messages.length ? messages.join('; ') : null;
    }
    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        for (const key of ['detail', 'message', 'error']) {
            const message = formatApiErrorValue(record[key]);
            if (message) return message;
        }
        try {
            return JSON.stringify(value);
        } catch {
            return null;
        }
    }
    return value == null ? null : String(value);
};

const formatApiError = (data: unknown, response: Response): string =>
    formatApiErrorValue(data)
    || `HTTP ${response.status}: ${response.statusText || 'Ошибка запроса'}`;

const getMerchantPaymentAnalyticsProperties = (
    data: any,
    operationType: 'deposit' | 'withdraw',
    merchantId?: string
): AnalyticsProperties => ({
    merchant_id: merchantId,
    operation_type: operationType,
    payment_id: data?.payment_id,
    transaction_id: data?.transaction_id,
    status: data?.status,
    amount: data?.amount,
    fiat_amount: data?.fiat_amount,
    payment_url_path: data?.payment_url,
    environment: data?.environment || 'live',
});

export const MerchantAPITestPage: React.FC = () => {
    const { user } = useMerchantAuth();
    const { trackPayment } = useMerchantPaymentPolling();
    const [apiKey] = useState(user?.api_key || '');
    const [userId, setUserId] = useState('test_user_123');
    const [amount, setAmount] = useState(1000);
    const [merchantPaymentId, setMerchantPaymentId] = useState(() => createMerchantId('pay'));
    const [merchantWithdrawalId, setMerchantWithdrawalId] = useState(() => createMerchantId('wd'));
    const [callback, setCallback] = useState('');
    const [requisiteType, setRequisiteType] = useState<'all' | RequisiteType>('all');
    const [operationType, setOperationType] = useState<'deposit' | 'withdraw'>('deposit');
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    // Валюты зависят от типа операции
    const fromCurrency = operationType === 'deposit' ? 'RUB' : 'USDT';
    const toCurrency = operationType === 'deposit' ? 'USDT' : 'RUB';

    const handleCreatePayment = async () => {
        if (!apiKey.trim()) {
            setError('Введите API ключ');
            return;
        }

        setLoading(true);
        setError(null);
        setResponse(null);

        try {
            // Выбираем правильный endpoint в зависимости от типа операции
            const endpoint = operationType === 'deposit'
                ? `${API_BASE_URL}/api/merchant/payments`
                : `${API_BASE_URL}/api/merchant/withdrawals`;
            const callbackUrl = callback.trim();
            const requestPayload: Record<string, string | number> = {
                user_id: userId,
                amount: amount,
                ...(operationType === 'deposit'
                    ? { merchantPaymentId: merchantPaymentId }
                    : { merchantWithdrawalId: merchantWithdrawalId })
            };
            if (callbackUrl) {
                requestPayload.callback = callbackUrl;
            }
            if (requisiteType !== 'all') {
                requestPayload.requisiteType = requisiteType;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${apiKey}`,
                },
                body: JSON.stringify(requestPayload)
            });

            // Проверяем, что ответ действительно JSON
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                throw new Error(`Сервер вернул не JSON ответ. Content-Type: ${contentType}. Ответ: ${text.substring(0, 200)}...`);
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(formatApiError(data, response));
            }

            setResponse(data);
            trackAnalyticsEvent(ANALYTICS_EVENTS.MERCHANT_PAYMENT_CREATED, {
                ...getMerchantPaymentAnalyticsProperties(data, operationType, user?.id),
                customer_id: userId,
                request_amount: amount,
                has_callback: Boolean(callbackUrl),
            });

            // Уведомляем о создании платежа
            merchantEvents.emit(MERCHANT_EVENTS.PAYMENT_CREATED);
            console.log('Событие создания платежа отправлено');

            // Добавляем платеж в отслеживание статуса
            if (data.transaction_id) {
                trackPayment(data.transaction_id, 'assigned');
                console.log(`📍 Платеж ${data.transaction_id} добавлен в отслеживание`);
            }
        } catch (err: any) {
            console.error('Ошибка API запроса:', err);
            trackAnalyticsEvent(ANALYTICS_EVENTS.MERCHANT_PAYMENT_CREATE_FAILED, {
                merchant_id: user?.id,
                operation_type: operationType,
                customer_id: userId,
                request_amount: amount,
                has_callback: Boolean(callback.trim()),
                error_message: err.message,
            });
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            if (response?.payment_url) {
                trackAnalyticsEvent(ANALYTICS_EVENTS.MERCHANT_PAYMENT_LINK_COPIED, {
                    ...getMerchantPaymentAnalyticsProperties(response, operationType, user?.id),
                });
            }
        } catch (err) {
            console.error('Ошибка копирования:', err);
        }
    };

    const openPaymentLink = () => {
        if (!response?.payment_url) return;

        trackAnalyticsEvent(ANALYTICS_EVENTS.MERCHANT_PAYMENT_LINK_OPEN_CLICKED, {
            ...getMerchantPaymentAnalyticsProperties(response, operationType, user?.id),
        });
        window.open(`${paymentWebUrl()}${response.payment_url}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="min-w-0">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Тестирование Merchant API</h2>
                    <p className="text-gray-600 dark:text-slate-300 mt-2">
                        Протестируйте создание платежей через API
                    </p>
                </div>

                {/* Форма для тестирования */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Send className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                            Создать платеж
                        </CardTitle>
                        <CardDescription>
                            Введите параметры для создания тестового платежа
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded text-sm text-blue-800 dark:text-blue-200">
                            Используется ваш API ключ из аккаунта мерчанта. Ввод не требуется.
                        </div>

                        {/* Выбор типа операции */}
                        <div className="space-y-2">
                            <Label>Тип операции</Label>
                            <div className="grid grid-cols-2 gap-4">
                                <div
                                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${operationType === 'deposit'
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-700'
                                        }`}
                                    onClick={() => setOperationType('deposit')}
                                >
                                    <div className="text-center">
                                        <div className="text-lg font-semibold text-blue-600 dark:text-blue-300">📥 Пополнение</div>
                                        <div className="text-xs text-gray-600 dark:text-slate-300 mt-1">
                                            Тест API для обмена RUB → USDT
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${operationType === 'withdraw'
                                        ? 'border-green-500 bg-green-50 dark:bg-green-950'
                                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-700'
                                        }`}
                                    onClick={() => setOperationType('withdraw')}
                                >
                                    <div className="text-center">
                                        <div className="text-lg font-semibold text-green-600 dark:text-green-300">📤 Вывод</div>
                                        <div className="text-xs text-gray-600 dark:text-slate-300 mt-1">
                                            Тест API для вывода USDT → RUB
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Валютная пара (автоматически) */}
                        <div className="p-3 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg">
                            <div className="flex items-center justify-center gap-3 text-sm">
                                <div className="text-center">
                                    <div className="text-xs text-gray-600 dark:text-slate-300">Отдаю</div>
                                    <div className="font-semibold text-blue-600 dark:text-blue-300">{fromCurrency}</div>
                                </div>
                                <span className="text-gray-400">→</span>
                                <div className="text-center">
                                    <div className="text-xs text-gray-600 dark:text-slate-300">Получаю</div>
                                    <div className="font-semibold text-green-600 dark:text-green-300">{toCurrency}</div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="user_id">ID пользователя</Label>
                                <Input
                                    id="user_id"
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    placeholder="Уникальный ID пользователя"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="amount">
                                    Сумма в рублях (RUB)
                                </Label>
                                <Input
                                    id="amount"
                                    type="number"
                                    min="0"
                                    value={amount}
                                    onChange={(e) => setAmount(Number(e.target.value))}
                                    placeholder="1000"
                                />
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    {operationType === 'deposit'
                                        ? 'Сумма, которую пользователь хочет обменять на USDT'
                                        : 'Сумма, которую пользователь хочет получить при выводе'
                                    }
                                </p>
                            </div>

                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="merchant_id">
                                    {operationType === 'deposit' ? 'Merchant Payment ID' : 'Merchant Withdrawal ID'}
                                </Label>
                                <div className="flex gap-2">
                                    <Input
                                        id="merchant_id"
                                        value={operationType === 'deposit' ? merchantPaymentId : merchantWithdrawalId}
                                        onChange={(e) => {
                                            if (operationType === 'deposit') {
                                                setMerchantPaymentId(e.target.value);
                                            } else {
                                                setMerchantWithdrawalId(e.target.value);
                                            }
                                        }}
                                        placeholder={operationType === 'deposit' ? 'pay_...' : 'wd_...'}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            if (operationType === 'deposit') {
                                                setMerchantPaymentId(createMerchantId('pay'));
                                            } else {
                                                setMerchantWithdrawalId(createMerchantId('wd'));
                                            }
                                        }}
                                    >
                                        Новый
                                    </Button>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    Повтор с тем же ID и тем же payload вернет уже созданную транзакцию.
                                </p>
                            </div>

                            <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="requisite_type">Тип реквизита</Label>
                                    <select
                                        id="requisite_type"
                                        value={requisiteType}
                                        onChange={(event) => setRequisiteType(event.target.value as 'all' | RequisiteType)}
                                        className="h-10 w-full rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm"
                                    >
                                        <option value="all">Любой доступный</option>
                                        <option value="sbp">СБП</option>
                                        <option value="card">Банковская карта</option>
                                        <option value="mobile_kgs">Мобильный оператор</option>
                                        <option value="qr_vietnam_sber">QR Vietnam — Sber</option>
                                        <option value="qr_vietnam_vtb">QR Vietnam — VTB</option>
                                        <option value="psb_ecom">PSB E-com</option>
                                        <option value="rub_transgran_uzs_emission_tbank">rub_transgran_uzs_emission_tbank</option>
                                        <option value="rub_transgran_uzs_emission_vtb">rub_transgran_uzs_emission_vtb</option>
                                        <option value="rub_transgran_uzs_emission_sber">rub_transgran_uzs_emission_sber</option>
                                        <option value="transgran_card_rub_arm">transgran_card_rub_arm</option>
                                        <option value="transgran_mobile_rub_arm">transgran_mobile_rub_arm</option>
                                        <option value="elqr">ElQR</option>
                                    </select>
                                    <p className="text-xs text-gray-500 dark:text-slate-400">
                                        При выборе «Любой доступный» поле requisiteType не отправляется.
                                    </p>
                                </div>

                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="callback">Callback URL</Label>
                                <Input
                                    id="callback"
                                    value={callback}
                                    onChange={(e) => setCallback(e.target.value)}
                                    placeholder="https://merchant.example.com/webhook"
                                />
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    Если оставить пустым, webhook уйдет на URL из настроек мерчанта.
                                </p>
                            </div>
                        </div>

                        <Button
                            onClick={handleCreatePayment}
                            disabled={loading || !apiKey}
                            className="w-full"
                        >
                            {loading
                                ? (operationType === 'deposit' ? 'Создание платежа...' : 'Создание заявки на вывод...')
                                : (operationType === 'deposit' ? 'Создать платеж' : 'Создать заявку на вывод')
                            }
                        </Button>
                    </CardContent>
                </Card>

                {/* Ошибка */}
                {error && (
                    <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950">
                        <CardContent className="pt-6">
                            <div className="text-red-700 dark:text-red-300">
                                <h3 className="font-semibold">Ошибка</h3>
                                <p className="mt-1 text-sm">{error}</p>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Успешный ответ */}
                {response && (
                    <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-green-800 dark:text-green-200">
                                <Check className="h-5 w-5" />
                                Платеж создан успешно!
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-sm text-green-700 dark:text-green-300">ID платежа</Label>
                                    <p className="font-mono text-sm break-all">{response.payment_id}</p>
                                </div>
                                <div>
                                    <Label className="text-sm text-green-700 dark:text-green-300">ID транзакции</Label>
                                    <p className="font-mono text-sm break-all">{response.transaction_id}</p>
                                </div>
                                <div>
                                    <Label className="text-sm text-green-700 dark:text-green-300">Статус</Label>
                                    <Badge variant="secondary">{response.status}</Badge>
                                </div>
                                <div>
                                    <Label className="text-sm text-green-700 dark:text-green-300">Сумма</Label>
                                    <p className="font-semibold">{response.amount} RUB</p>
                                </div>
                                <div>
                                    <Label className="text-sm text-green-700 dark:text-green-300">Курс</Label>
                                    <p className="font-semibold">{response.exchange_rate ?? '-'}</p>
                                </div>
                                <div>
                                    <Label className="text-sm text-green-700 dark:text-green-300">USDT к зачислению</Label>
                                    <p className="font-semibold">{response.merchant_credited_usdt ?? '-'}</p>
                                </div>
                                <div>
                                    <Label className="text-sm text-green-700 dark:text-green-300">Сумма в фиате</Label>
                                    <p className="font-semibold">{response.fiat_amount ?? '-'}{response.fiat_amount != null ? ' RUB' : ''}</p>
                                </div>
                            </div>

                            {/* Ссылка для оплаты */}
                            {response.payment_url && (
                                <div className="space-y-2">
                                    <Label className="text-sm text-green-700 dark:text-green-300">Ссылка для оплаты пользователем</Label>
                                    <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 border border-green-200 dark:border-green-800 rounded-md">
                                        <code className="flex-1 text-sm break-all">
                                            {`${paymentWebUrl()}${response.payment_url}`}
                                        </code>
                                        <div className="flex gap-1">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => copyToClipboard(`${paymentWebUrl()}${response.payment_url}`)}
                                            >
                                                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={openPaymentLink}
                                            >
                                                <ExternalLink className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                    <p className="text-xs text-green-600 dark:text-green-300">
                                        Отправьте эту ссылку пользователю для оплаты
                                    </p>
                                </div>
                            )}

                            {/* JSON ответ */}
                            <div className="space-y-2">
                                <Label className="text-sm text-green-700 dark:text-green-300">Полный ответ API</Label>
                                <div className="p-3 bg-white dark:bg-slate-800 border border-green-200 dark:border-green-800 rounded-md">
                                    <pre className="text-xs text-gray-700 dark:text-slate-200 overflow-auto">
                                        {JSON.stringify(response, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Пример кода */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Code className="h-5 w-5 text-gray-600 dark:text-slate-300" />
                            Пример кода
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="p-4 bg-gray-900 text-gray-100 rounded-md text-sm">
                            <pre>{`// JavaScript/Node.js пример

// Для пополнения (RUB → USDT)
const depositResponse = await fetch('${API_BASE_URL}/api/merchant/payments', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY'
  },
  body: JSON.stringify({
    user_id: 'user123',
    amount: 1000,  // Сумма в RUB
    merchantPaymentId: 'pay_20260101_0001',
    requisiteType: 'mobile_kgs', // Опционально: 'sbp', 'card' или 'mobile_kgs'
    callback: 'https://merchant.example.com/payments/webhook'
  })
});

// Для вывода (USDT → RUB)
const withdrawResponse = await fetch('${API_BASE_URL}/api/merchant/withdrawals', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY'
  },
  body: JSON.stringify({
    user_id: 'user123',
    amount: 5000,  // Сумма в RUB
    merchantWithdrawalId: 'wd_20260101_0001',
    requisiteType: 'mobile_kgs',
    callback: 'https://merchant.example.com/withdrawals/webhook'
  })
});

const payment = await depositResponse.json();
console.log('Ссылка для оплаты:', payment.payment_url);`}</pre>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
